import React, { FC, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment, MobileHeroQueryQuery } from "@graphqlTypes"

import Hero from "@components/Hero/Hero"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"
import MobileAppButtons from "@components/MobileAppButtons/MobileAppButtons"

import "./MobileHero.scss"

type MobileHeroJsonPayload = {
  title: string,
  bodyText: string,
  appleAppStoreUrl: string,
  googlePlayStoreUrl: string,
}

const MobileHero: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const {
      mobileHeroRight1,
      mobileHeroRight2,
      mobileHeroRight3,
      mobileHeroLeft1,
      mobileHeroLeft2,
      mobileHeroLeft3,
      mobileHeroPhone,
      mobileHeroPhoneItem,
    } = useStaticQuery<MobileHeroQueryQuery>(graphql`
    query MobileHeroQuery {
      mobileHeroRight1: contentfulAsset(contentful_id: {eq: "1dqYvZ8sr9F0uIawVHJPS0"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroRight2: contentfulAsset(contentful_id: {eq: "7b5rss95fUsj1Btxmm5FlY"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroRight3: contentfulAsset(contentful_id: {eq: "7fnKyuaoySdK2e7CHkcXbL"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroLeft1: contentfulAsset(contentful_id: {eq: "5ZOwqWw0cNNnEbA6nyfJeX"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroLeft2: contentfulAsset(contentful_id: {eq: "16iIvtwMesSVkSXCRnwELq"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroLeft3: contentfulAsset(contentful_id: {eq: "6Zo6Np77K9SKGs2hvQZFa3"}) {
        imgixImage {
          gatsbyImageData(width: 413)
        }
      }
      mobileHeroPhone: contentfulAsset(contentful_id: {eq: "6Wl4KvTyMnrFOG9ZC2Z3D4"}) {
        imgixImage {
          gatsbyImageData(width: 339)
        }
      }
      mobileHeroPhoneItem: contentfulAsset(
        contentful_id: {eq: "6Ouq8MEglxDzjTeDkedRV4"}
      ) {
        imgixImage {
          gatsbyImageData(width: 527)
        }
      }
    }
  `)
    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as MobileHeroJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: HomeHero (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    return (
      <Hero className="MobileHero">
        <div className="container">
          <div className="grid">
            <div className="column content">
              <h1 dangerouslySetInnerHTML={{ __html: json.title }} />
              <p className="font-size--L" dangerouslySetInnerHTML={{ __html: json.bodyText }} />
              <MobileAppButtons />
            </div>
          </div>
        </div>
        <div className="image-comp">
          {mobileHeroPhone?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt="Mobile Phone"
              aspectRatio={678 / 1400}
              className="image--mobileHeroPhone"
              imageData={mobileHeroPhone.imgixImage.gatsbyImageData}
            />
          )}
          {mobileHeroPhoneItem?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt="Mobile Phone List Item"
              aspectRatio={678 / 1400}
              className="image--mobileHeroPhoneItem"
              imageData={mobileHeroPhoneItem.imgixImage.gatsbyImageData}
            />
          )}
          {/* Left side items */}
          {mobileHeroLeft1?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroLeft1"
              imageData={mobileHeroLeft1.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {mobileHeroLeft2?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroLeft2"
              imageData={mobileHeroLeft2.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {mobileHeroLeft3?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroLeft3"
              imageData={mobileHeroLeft3.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {/* Right side items */}
          {mobileHeroRight1?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroRight1"
              imageData={mobileHeroRight1.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {mobileHeroRight2?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroRight2"
              imageData={mobileHeroRight2.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {mobileHeroRight3?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={826 / 208}
              className="image--reply image--mobileHeroRight3"
              imageData={mobileHeroRight3.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
        </div>
      </Hero>
    )
  }

export default MobileHero
